import { useEffect, useMemo, useState } from "react";
import { Breadcrumb, Button, Empty, message, Tabs, Tag } from "antd";
import { DownloadOutlined, EnvironmentOutlined, TeamOutlined, UserOutlined } from "@ant-design/icons";
import { useHistory } from "react-router-dom";
import { useQuery } from "../../common/utils";
import projectDefault from "../../img/project_default.jpg";
import {
  fetchMySaleActivityLearnInfo,
  fetchSaleActivityInfo,
} from "./api";
import {
  canSaleActivityExpertSignUp,
  canSaleActivityUserSignUp,
  getSaleActivityAuditState,
  getSaleActivityTimeStatusText,
  isSaleActivityEnded,
} from "./status";
import {
  SaleActivityInfo,
  SaleActivityLearnInfoResponse,
  SaleActivityLiveInfo,
  SaleActivityProjectInfo,
  SaleActivityTeachingActivityInfo,
  SaleAttachment,
} from "./types";
import "./activity.scss";

const BASE_URL = process.env.REACT_APP_BASE_URL || "";

function textOrDash(value?: string | number) {
  return value || "--";
}

function isAbsoluteUrl(url: string) {
  return /^https?:\/\//i.test(url);
}

function toAbsoluteUrl(url?: string) {
  if (!url) return "";
  if (isAbsoluteUrl(url)) return url;
  if (url.startsWith("/")) return `${BASE_URL}${url}`;
  return `${BASE_URL}/${url}`;
}

function getFileNameFromUrl(url: string, fallback: string) {
  const pureUrl = url.split("?")[0];
  try {
    return decodeURIComponent(pureUrl.split("/").filter(Boolean).pop() || fallback);
  } catch {
    return pureUrl.split("/").filter(Boolean).pop() || fallback;
  }
}

function getAttachmentUrl(item: SaleAttachment) {
  return item.fullPathFile || item.fileUrl || item.url || item.shortPathFile || "";
}

function getAttachmentName(item: SaleAttachment, index: number) {
  return item.fileName || item.name || getFileNameFromUrl(getAttachmentUrl(item), `附件${index + 1}`);
}

function formatFileSize(size?: number) {
  if (!size) return "";
  if (size < 1024 * 1024) return `${Math.ceil(size / 1024)}KB`;
  return `${Math.ceil(size / 1024 / 1024)}M`;
}

function buildFileAttachment(url?: string, fallback = "附件"): SaleAttachment[] {
  if (!url) return [];
  return [{
    fileName: getFileNameFromUrl(url, fallback),
    fileUrl: url,
    url,
  }];
}

function getActivityResponseData(res: any): SaleActivityLearnInfoResponse {
  return res?.data?.data || res?.data || res || {};
}

function getHostUnitText(info: SaleActivityInfo | null) {
  const source = (info || {}) as any;
  return textOrDash(
    source.hostUnit ||
      source.hostUnitName ||
      source.instituteName ||
      source.organizer ||
      source.organizerUnit ||
      source.sponsorUnit ||
      source.activityInfo?.hostUnit
  );
}

function InfoCell({ label, value }: { label: string; value: string | number }) {
  return (
    <>
      <div className="sale-activity-learn-info-label">{label}</div>
      <div className="sale-activity-learn-info-value">{value}</div>
    </>
  );
}

function InfoRow({ label, value }: { label: string; value: string | number }) {
  return (
    <>
      <div className="sale-activity-learn-info-label">{label}</div>
      <div className="sale-activity-learn-info-value sale-activity-learn-info-value--wide">{value}</div>
    </>
  );
}

function StatusSummaryItem({ label, value }: { label: string; value: string | number }) {
  return (
    <span className="sale-activity-learn-status-summary__item">
      <em>{label}：</em>
      <strong>{value}</strong>
    </span>
  );
}

function hasPassedText(...texts: Array<string | undefined>) {
  return texts.some((text) => {
    if (!text) return false;
    return ["已报名", "报名成功", "审核通过", "已认证", "认证通过", "已参加"].some((keyword) =>
      text.includes(keyword)
    );
  });
}

type RegisteredRole = "expert" | "user";

function getRegisteredRoles(info: SaleActivityInfo | null): RegisteredRole[] {
  if (!info) return [];
  const userState = getSaleActivityAuditState(info, "user");
  const expertState = getSaleActivityAuditState(info, "expert");
  const roles: RegisteredRole[] = [];
  if (
    expertState.passed ||
    hasPassedText(info.expertAuditStatusText, info.expertHireResultStatusText, info.expertHireStatusText)
  ) {
    roles.push("expert");
  }
  if (
    userState.passed ||
    hasPassedText(info.userAuditStatusText, info.userSignUpResultStatusText, info.userSignUpStatusText)
  ) {
    roles.push("user");
  }
  return roles;
}

function getLiveRoomTypeParam(roles: RegisteredRole[]) {
  if (roles.length > 1) return "both";
  return roles[0] || "";
}

function AttachmentLinks({ list }: { list?: SaleAttachment[] }) {
  if (!list || list.length === 0) return null;
  return (
    <div className="sale-activity-detail-attachments">
      {list.map((item, index) => {
        const url = toAbsoluteUrl(getAttachmentUrl(item));
        const fileName = getAttachmentName(item, index);
        return (
          <a key={item.id || `${fileName}-${index}`} href={url} target="_blank" rel="noreferrer">
            <DownloadOutlined />
            <span>{`附件${index + 1}： ${fileName}`}</span>
            {item.fileSize ? <em>{formatFileSize(item.fileSize)}</em> : null}
          </a>
        );
      })}
    </div>
  );
}

export default function SaleActivityDetail() {
  const history = useHistory();
  const query = useQuery();
  const activityId = query.get("activityId") || "";
  const [info, setInfo] = useState<SaleActivityInfo | null>(null);
  const [loading, setLoading] = useState(false);
  const [resourceLoading, setResourceLoading] = useState(false);
  const [projectList, setProjectList] = useState<SaleActivityProjectInfo[]>([]);
  const [liveActivityList, setLiveActivityList] = useState<SaleActivityLiveInfo[]>([]);
  const [teachingActivityList, setTeachingActivityList] = useState<SaleActivityTeachingActivityInfo[]>([]);

  useEffect(() => {
    if (!activityId) return;
    setLoading(true);
    fetchSaleActivityInfo(activityId)
      .then((res) => {
        const data = getActivityResponseData(res);
        setInfo(data.activityInfo ? { ...data, ...data.activityInfo } : (data as SaleActivityInfo));
        setProjectList(data.projectList || []);
        setLiveActivityList(data.liveActivityList || []);
        setTeachingActivityList(data.teachingActivityList || []);
      })
      .finally(() => setLoading(false));
  }, [activityId]);

  const registeredRoles = useMemo(() => getRegisteredRoles(info), [info]);
  const registeredType = registeredRoles.includes("user") ? "user" : registeredRoles[0] || "";
  const liveRoomType = getLiveRoomTypeParam(registeredRoles);

  useEffect(() => {
    if (!activityId || !registeredType) {
      setResourceLoading(false);
      return;
    }

    let canceled = false;
    setResourceLoading(true);
    fetchMySaleActivityLearnInfo(registeredType, activityId)
      .then((res) => {
        if (canceled) return;
        const data = (res.data || {}) as SaleActivityLearnInfoResponse;
        setProjectList(data.projectList || []);
        setLiveActivityList(data.liveActivityList || []);
        setTeachingActivityList(data.teachingActivityList || []);
      })
      .finally(() => {
        if (!canceled) setResourceLoading(false);
      });

    return () => {
      canceled = true;
    };
  }, [activityId, registeredType]);

  const expertLimit = info?.expertNumLimit || info?.signUpExpertNum || 0;
  const userLimit = info?.signUpNumLimit || info?.signUpUserNum || info?.signUpNum || 0;
  const hostUnitText = getHostUnitText(info);
  const activityTypeList = [
    liveActivityList.length > 0 ? "直播授课" : "",
    projectList.length > 0 ? "点播课程" : "",
    teachingActivityList.length > 0 ? "线下授课" : "",
  ].filter(Boolean);
  const activityTypeText = activityTypeList.length > 0 ? activityTypeList.join("、") : "--";
  const activityStatusText = info?.activityLearnStatusText || "--";
  const expertApplyStatusText =
    info?.expertHireResultStatusText ||
    info?.expertAuditStatusText ||
    info?.expertHireStatusText ||
    "--";
  const userApplyStatusText =
    info?.userSignUpResultStatusText ||
    info?.userAuditStatusText ||
    info?.userSignUpStatusText ||
    "--";
  const expertRecruitStatusText = getSaleActivityTimeStatusText(info, "expert");
  const userRecruitStatusText = getSaleActivityTimeStatusText(info, "user");
  const pointRuleText = info?.activityRule || (info?.pointTotal ? `${info.pointTotal}积分` : "--");
  const canExpertSignUp = canSaleActivityExpertSignUp(info);
  const canUserSignUp = canSaleActivityUserSignUp(info);
  const expertAttachments = useMemo(() => [
    ...buildFileAttachment(info?.expertHireFile, "专家招募附件"),
    ...(info?.saleExpertAttachmentList || []),
  ], [info]);
  const resourceCover = info?.pictureUrl || projectDefault;
  const isActivityEnded = isSaleActivityEnded(info);
  const isStudyEnded = (item: SaleActivityProjectInfo) =>
    isActivityEnded ||
    Number(item.studyStatus) === 2 ||
    item.studyStatusText === "已结束";
  const openProject = (item: SaleActivityProjectInfo) => {
    const studyStatus = Number(item.studyStatus);
    const timeRange = `${textOrDash(item.startTimeText)} - ${textOrDash(item.endTimeText)}`;
    if (isActivityEnded) {
      message.warning("活动已结束，无法进入点播课程");
      return;
    }
    if (studyStatus === 0 || item.studyStatusText === "未开始") {
      message.warning(`不在学习时间范围内，学习时间：${timeRange}`);
      return;
    }
    if (isStudyEnded(item)) {
      message.warning(`点播课程已结束，学习时间：${timeRange}`);
      return;
    }
    history.push(`/saleActivityProjectInfo?saleActivityId=${activityId}&projectId=${item.projectId}`);
  };
  const openLiveRoomList = (item: SaleActivityLiveInfo) => {
    const saleActivityDetailId = item.saleActivityDetailId;
    if (!saleActivityDetailId || !registeredType) return;
    const params = new URLSearchParams({
      saleActivityId: String(item.saleActivityId || activityId),
      saleActivityDetailId: String(saleActivityDetailId),
      type: liveRoomType,
      liveActivityName: item.liveActivityName || "直播授课",
      cover: resourceCover,
    });
    history.push(`/saleActivityLiveRoomList?${params.toString()}`);
  };
  const getOfflineDetailId = (item: SaleActivityTeachingActivityInfo) =>
    item.saleActivityDetailId || item.teachingActivityId;

  if (!activityId) {
    return <div className="sale-activity-detail-page"><Empty description="缺少活动参数" /></div>;
  }

  if (!info && !loading) {
    return <div className="sale-activity-detail-page"><Empty description="暂无活动信息" /></div>;
  }

  return (
    <div className="sale-activity-detail-page">
      <div className="sale-activity-detail-breadcrumb">
        <Breadcrumb separator=">">
          <Breadcrumb.Item>活动报名</Breadcrumb.Item>
          <Breadcrumb.Item>活动详情</Breadcrumb.Item>
        </Breadcrumb>
      </div>

      <div className="sale-activity-detail-main">
        <div className="sale-activity-detail-title-row">
          <div className="sale-activity-learn-title-main">
            <h2>{info?.name || "--"}</h2>
            <Tag className="sale-activity-detail-status">活动状态：{activityStatusText}</Tag>
          </div>
          <div className="sale-activity-learn-status-summary">
            <StatusSummaryItem label="学员报名" value={userApplyStatusText} />
            <StatusSummaryItem label="专家报名" value={expertApplyStatusText} />
          </div>
        </div>
        <div className="sale-activity-detail-summary">
          <img src={info?.pictureUrl || projectDefault} alt={info?.name || "活动图片"} />
          <div className="sale-activity-detail-meta">
            <div className="sale-activity-learn-info-grid sale-activity-detail-info-grid">
              <InfoRow label="活动类型" value={activityTypeText} />
              <InfoRow label="主办单位" value={hostUnitText} />
              <InfoRow label="活动时间" value={`${textOrDash(info?.beginTimeText || info?.beginTime)} - ${textOrDash(info?.endTimeText || info?.endTime)}`} />
              <InfoRow label="活动状态" value={activityStatusText} />
            </div>
            <div className="sale-activity-detail-actions">
              <Button
                className="sale-activity-detail-actions__expert"
                icon={<UserOutlined />}
                disabled={!canExpertSignUp}
                onClick={() => history.push(`/saleActivityExpertSignUp?activityId=${activityId}`)}
              >
                专家报名
              </Button>
              <Button
                className="sale-activity-detail-actions__user"
                icon={<TeamOutlined />}
                disabled={!canUserSignUp}
                onClick={() => history.push(`/saleActivityUserSignUp?activityId=${activityId}`)}
              >
                学员报名
              </Button>
            </div>
          </div>
        </div>
      </div>

      <div className="sale-activity-detail-content">
        <Tabs defaultActiveKey="content">
          <Tabs.TabPane key="content" tab="活动介绍">
            <div className="sale-activity-html" dangerouslySetInnerHTML={{ __html: info?.activityContent || "暂无活动介绍" }} />
            <AttachmentLinks list={info?.saleAttachmentList || []} />
          </Tabs.TabPane>
          <Tabs.TabPane key="recruit" tab="招募信息">
            <div className="sale-activity-learn-info-grid sale-activity-detail-tab-info-grid">
              <InfoCell label="计划招募专家" value={expertLimit ? `${expertLimit}人` : "--"} />
              <InfoCell label="专家招募状态" value={expertRecruitStatusText} />
              <InfoRow label="专家招募时间" value={`${textOrDash(info?.expertHireBeginTime)} - ${textOrDash(info?.expertHireEndTime)}`} />
              <InfoCell label="计划招募学员" value={userLimit ? `${userLimit}人` : "--"} />
              <InfoCell label="学员招募状态" value={userRecruitStatusText} />
              <InfoRow label="学员招募时间" value={`${textOrDash(info?.signUpBeginTime)} - ${textOrDash(info?.signUpEndTime)}`} />
              <InfoRow label="积分规则" value={pointRuleText} />
            </div>
          </Tabs.TabPane>
          <Tabs.TabPane key="expert" tab="专家招募通知">
            <div className="sale-activity-html" dangerouslySetInnerHTML={{ __html: info?.expertHireContent || "暂无专家招募通知" }} />
            <AttachmentLinks list={expertAttachments} />
          </Tabs.TabPane>
          <Tabs.TabPane key="user" tab="学员招募通知">
            <div className="sale-activity-html" dangerouslySetInnerHTML={{ __html: info?.userHireContent || "暂无学员招募通知" }} />
          </Tabs.TabPane>
        </Tabs>
      </div>

      {registeredRoles.length > 0 && (
        <div className="sale-activity-learn-resources sale-activity-detail-resources">
          <div className="sale-activity-learn-resource-section">
            <h3>直播授课</h3>
            {liveActivityList.length > 0 ? (
              <div className="sale-activity-resource-list">
                {liveActivityList.map((item) => (
                  <div className="sale-activity-resource-card sale-activity-resource-card--live" key={item.liveActivityId} onClick={() => openLiveRoomList(item)}>
                    <img src={resourceCover} alt={item.liveActivityName || "直播授课"} />
                    <h4>{item.liveActivityName}</h4>
                    <p>开始时间：{textOrDash(item.beginTimeText || item.beginTime)}</p>
                    <p>结束时间：{textOrDash(item.endTimeText || item.endTime)}</p>
                  </div>
                ))}
              </div>
            ) : <Empty description={resourceLoading ? "课程加载中" : "暂无直播授课"} />}
          </div>

          <div className="sale-activity-learn-resource-section">
            <h3>点播课程</h3>
            {projectList.length > 0 ? (
              <div className="sale-activity-resource-list">
                {projectList.map((item) => (
                  <div className="sale-activity-resource-card" key={item.projectId} onClick={() => openProject(item)}>
                    <img src={item.projectPicture || item.pictureUrl || resourceCover} alt={item.projectName || "点播课程"} />
                    <h4>{item.projectName}</h4>
                    <p>开始时间：{textOrDash(item.startTimeText)}</p>
                    <p>结束时间：{textOrDash(item.endTimeText)}</p>
                  </div>
                ))}
              </div>
            ) : <Empty description={resourceLoading ? "课程加载中" : "暂无点播课程"} />}
          </div>

          <div className="sale-activity-learn-resource-section">
            <h3>线下授课</h3>
            {teachingActivityList.length > 0 ? (
              <div className="sale-activity-resource-list">
                {teachingActivityList.map((item) => (
                  <div className="sale-activity-resource-card" key={item.teachingActivityId} onClick={() => history.push(`/saleActivityOfflineDetail?saleActivityId=${activityId}&activityDetailId=${getOfflineDetailId(item)}&teachingActivityId=${item.teachingActivityId}&type=${registeredType}`)}>
                    <img src={resourceCover} alt={item.teachingActivityName || "线下授课"} />
                    <h4>{item.teachingActivityName}</h4>
                    <p>开始时间：{textOrDash(item.beginTimeText || item.startTime)}</p>
                    <p><EnvironmentOutlined /> {textOrDash(item.activityAddress)}</p>
                  </div>
                ))}
              </div>
            ) : <Empty description={resourceLoading ? "课程加载中" : "暂无线下授课"} />}
          </div>
        </div>
      )}
    </div>
  );
}
